import '../../common/EsCommon.css';
import { useEsDashboardFlag } from '../../common/EsFlags';
import { useEsStrings } from '../../common/EsStrings';
import EsCampaigns from '../campaign/EsCampaigns';

/**
 * /view/console -- the Console, or the news that it is not on yet.
 *
 * <p><b>The flag branch is here rather than in a route wrapper, and that is the
 * whole point of this file.</b> Every other flagged page in the app is wrapped
 * in EsRequireFlag, which redirects to the home page when the flag is off --
 * correct for the codex and characters, whose routes did not exist before their
 * flag. This route did. The Dash entry in the nav is unconditional and every
 * logged-in reader can already click it, so a gate would send them to the
 * marketing home page for as long as rolloutEsConsole is off. That is not
 * leaving the current behaviour alone; it is a regression shipped behind an off
 * flag.
 *
 * <p>Three states, not two. `ready` is the reason useEsDashboardFlag returns a
 * pair: "LaunchDarkly has not answered" and "the answer is false" are different
 * things, and rendering the off branch during the first is a page that says the
 * feature is missing and then replaces itself. It is the same guard
 * EsRequireFlag carries for the same reason.
 *
 * <p>The on branch is EsCampaigns -- the same component /view/campaigns renders.
 * One list, two routes, not a second implementation of it. Phase 4 replaces
 * this branch with the moment, from the keyless GET campaignDash.
 *
 * <p>The message is a literal. There is no message catalog in esfront -- no
 * messages.properties, no EsEnv.msg; those are esback's, for the X* exceptions
 * -- and every user-facing string in this app is a literal in JSX. It could not
 * come from the server anyway: this branch runs before any API call, off a value
 * the client already holds, and asking esback would mean a request EsFlagFilter
 * answers 404 to.
 */
export default function EsDash() {
    const t = useEsStrings();
    const { ready, on } = useEsDashboardFlag();

    if ( !ready ) return null;

    if ( on ) return <EsCampaigns />;

    return (
        <section className="relative z-10 pb-4 pt-nav-room">
            <div className="container">
                <div className="flex flex-wrap mx-[-16px]">
                    <div className="w-full px-4">
                        { /* es-dash-* is this page's own prefix and the off
                             branch is the only thing wearing it today, so
                             phase 4's panels extend the set rather than
                             renaming anything. The on branch is EsCampaigns
                             and answers to es-campaign-*. */ }
                        <div className="mx-auto rounded-md p-12 max-w-[500px] sm:p-[60px]"
                             data-testid="es-dash-unavailable">
                            <h3 className="mb-3 text-center text-2xl font-bold text-heading sm:text-3xl"
                                data-testid="es-dash-unavailable-message">
                                { t( 'dash-unavailable', 'The Console is not yet available' ) }
                            </h3>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
